import type { Song, YouTubePlayer } from '@/types';
import { PlayerState, ZemerError } from '@/types';

declare global {
  interface Window {
    YT: {
      Player: new (elementId: string, config: any) => YouTubePlayer;
      PlayerState: typeof PlayerState;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

type PlayerEventCallback = (event: { data: number }) => void;
type PlayerErrorCallback = (event: { data: number }) => void;

interface PlayerCallbacks {
  onReady?: () => void;
  onStateChange?: PlayerEventCallback;
  onError?: PlayerErrorCallback;
}

export class YouTubePlayerManager {
  private player: YouTubePlayer | null = null;
  private ready = false;
  private currentSong: Song | null = null;
  private callbacks: PlayerCallbacks = {};

  constructor() {
    this.loadYouTubeAPI();
  }

  private loadYouTubeAPI(): void {
    // Only load the script if not already loaded
    // Don't set ready here - that happens in initialize() when player is created
    if (window.YT) {
      return;
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag?.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      document.head.appendChild(tag);
    }
  }

  initialize(elementId: string, callbacks: PlayerCallbacks): Promise<void> {
    this.callbacks = callbacks;

    return new Promise((resolve, reject) => {
      // Timeout after 15 seconds
      const timeout = setTimeout(() => {
        reject(new ZemerError('YouTube API failed to load', 'PLAYER_TIMEOUT'));
      }, 15000);

      const initPlayer = () => {
        // Don't clear timeout yet - wait for onReady
        try {
          // Check if element exists
          const element = document.getElementById(elementId);
          if (!element) {
            clearTimeout(timeout);
            reject(new ZemerError(`Element #${elementId} not found`, 'ELEMENT_NOT_FOUND'));
            return;
          }

          this.player = new window.YT.Player(elementId, {
            height: '1',
            width: '1',
            playerVars: {
              playsinline: 1,
              controls: 0,
              autoplay: 1,
              enablejsapi: 1,
              origin: window.location.origin,
            },
            events: {
              onReady: () => {
                clearTimeout(timeout);
                this.ready = true;
                console.log('YouTube player ready');
                this.callbacks.onReady?.();
                resolve();
              },
              onStateChange: (e: any) => {
                this.callbacks.onStateChange?.(e);
                this.updateMediaSession();
              },
              onError: (e: any) => {
                console.error('YouTube player error:', e.data);
                this.callbacks.onError?.(e);
              },
            },
          });
        } catch (error) {
          clearTimeout(timeout);
          console.error('Failed to create YouTube player:', error);
          reject(new ZemerError('Failed to initialize player', 'PLAYER_INIT_ERROR', { error }));
        }
      };

      if (window.YT && window.YT.Player) {
        initPlayer();
      } else {
        window.onYouTubeIframeAPIReady = initPlayer;
      }
    });
  }

  loadSong(song: Song, startTime = 0): void {
    if (!this.player || !this.ready) {
      throw new ZemerError('Player not ready', 'PLAYER_NOT_READY');
    }

    this.currentSong = song;
    this.player.loadVideoById(song.videoId, startTime);
    this.updateMediaSession();
  }

  play(): void {
    if (!this.player || !this.ready) return;
    this.player.playVideo();
  }

  pause(): void {
    if (!this.player || !this.ready) return;
    this.player.pauseVideo();
  }

  togglePlay(): void {
    if (!this.player || !this.ready) return;
    const state = this.player.getPlayerState();
    if (state === PlayerState.PLAYING) {
      this.pause();
    } else {
      this.play();
    }
  }

  seek(seconds: number): void {
    if (!this.player || !this.ready) return;
    this.player.seekTo(seconds, true);
  }

  setVolume(volume: number): void {
    if (!this.player || !this.ready) return;
    // YouTube player uses 0-100 scale
    const ytVolume = Math.max(0, Math.min(100, Math.round(volume * 100)));
    this.player.setVolume(ytVolume);
  }

  getVolume(): number {
    if (!this.player || !this.ready) return 1;
    try {
      return this.player.getVolume() / 100;
    } catch {
      return 1;
    }
  }

  mute(): void {
    if (!this.player || !this.ready) return;
    this.player.mute();
  }

  unMute(): void {
    if (!this.player || !this.ready) return;
    this.player.unMute();
  }

  isMuted(): boolean {
    if (!this.player || !this.ready) return false;
    try {
      return this.player.isMuted();
    } catch {
      return false;
    }
  }

  getCurrentTime(): number {
    if (!this.player || !this.ready) return 0;
    try {
      return this.player.getCurrentTime();
    } catch {
      return 0;
    }
  }

  getDuration(): number {
    if (!this.player || !this.ready) return 0;
    try {
      return this.player.getDuration();
    } catch {
      return 0;
    }
  }

  getState(): number {
    if (!this.player || !this.ready) return PlayerState.UNSTARTED;
    try {
      return this.player.getPlayerState();
    } catch {
      return PlayerState.UNSTARTED;
    }
  }

  isPlaying(): boolean {
    return this.getState() === PlayerState.PLAYING;
  }

  isReady(): boolean {
    return this.ready;
  }

  getCurrentSong(): Song | null {
    return this.currentSong;
  }

  private updateMediaSession(): void {
    if (!('mediaSession' in navigator) || !this.currentSong) return;

    try {
      // Update metadata
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.currentSong.title,
        artist: this.currentSong.artist,
        album: 'Zemer Music',
        artwork: this.currentSong.thumbnail
          ? [
              {
                src: this.currentSong.thumbnail,
                sizes: '96x96',
                type: 'image/jpeg',
              },
              {
                src: this.currentSong.thumbnail.replace(/w\d+-h\d+/, 'w256-h256'),
                sizes: '256x256',
                type: 'image/jpeg',
              },
              {
                src: this.currentSong.thumbnail.replace(/w\d+-h\d+/, 'w512-h512'),
                sizes: '512x512',
                type: 'image/jpeg',
              },
            ]
          : [],
      });

      // Update playback state based on player state
      const state = this.getState();
      if (state === PlayerState.PLAYING) {
        navigator.mediaSession.playbackState = 'playing';
      } else if (state === PlayerState.PAUSED || state === PlayerState.BUFFERING) {
        navigator.mediaSession.playbackState = 'paused';
      } else {
        navigator.mediaSession.playbackState = 'none';
      }

      // Update position state immediately
      this.updatePositionState();
    } catch (error) {
      console.error('Failed to update media session:', error);
    }
  }

  setupMediaSessionHandlers(handlers: {
    onPlay?: () => void;
    onPause?: () => void;
    onNext?: () => void;
    onPrevious?: () => void;
    onSeek?: (time: number) => void;
    onStop?: () => void;
  }): void {
    if (!('mediaSession' in navigator)) return;

    try {
      // Play/Pause handlers
      if (handlers.onPlay) {
        navigator.mediaSession.setActionHandler('play', () => {
          console.log('Media session: play');
          handlers.onPlay?.();
        });
      }
      if (handlers.onPause) {
        navigator.mediaSession.setActionHandler('pause', () => {
          console.log('Media session: pause');
          handlers.onPause?.();
        });
      }

      // Track navigation
      if (handlers.onNext) {
        navigator.mediaSession.setActionHandler('nexttrack', () => {
          console.log('Media session: nexttrack');
          handlers.onNext?.();
        });
      }
      if (handlers.onPrevious) {
        navigator.mediaSession.setActionHandler('previoustrack', () => {
          console.log('Media session: previoustrack');
          handlers.onPrevious?.();
        });
      }

      // Seek to specific time
      if (handlers.onSeek) {
        navigator.mediaSession.setActionHandler('seekto', (details) => {
          if (details.seekTime !== undefined) {
            console.log('Media session: seekto', details.seekTime);
            handlers.onSeek?.(details.seekTime);
            this.updatePositionState();
          }
        });
      }

      // Stop handler
      if (handlers.onStop) {
        navigator.mediaSession.setActionHandler('stop', () => {
          console.log('Media session: stop');
          handlers.onStop?.();
        });
      }

      // Seek backward/forward by 10 seconds
      navigator.mediaSession.setActionHandler('seekbackward', (details) => {
        const skipTime = details.seekOffset || 10;
        const current = this.getCurrentTime();
        console.log('Media session: seekbackward', skipTime);
        this.seek(Math.max(0, current - skipTime));
        this.updatePositionState();
      });

      navigator.mediaSession.setActionHandler('seekforward', (details) => {
        const skipTime = details.seekOffset || 10;
        const current = this.getCurrentTime();
        const duration = this.getDuration();
        console.log('Media session: seekforward', skipTime);
        this.seek(Math.min(duration, current + skipTime));
        this.updatePositionState();
      });

      console.log('Media session handlers set up successfully');
    } catch (error) {
      console.error('Failed to setup media session handlers:', error);
    }
  }

  updatePositionState(): void {
    if (!('mediaSession' in navigator) || !('setPositionState' in navigator.mediaSession)) {
      return;
    }

    try {
      const duration = this.getDuration();
      const position = this.getCurrentTime();

      if (duration > 0) {
        navigator.mediaSession.setPositionState({
          duration,
          position,
          playbackRate: 1,
        });
      }
    } catch (error) {
      // Ignore errors - position state is optional
    }
  }
}

// Export singleton
export const playerManager = new YouTubePlayerManager();
